import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import * as angular from 'angular';
import { downgradeComponent } from '@angular/upgrade/static';
import { NgForm } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngrx/store';
import { AuthService, Credentials } from '../../common/services/auth.service';
import { NotifyService } from '../../common/services/notify.service';
import { AppState } from '../../store';
import { loginSuccess } from '../store/account.actions';
import { PusherService } from '../../common/services/pusher.service';
import { getIntegrateIOFlag, getSSOFlag, redirectToIntegrateIOLogin } from '../helpers/account.helpers';

// cross-check name with back-end controller
const cookieName = 'stlzr_tkn';

function getCookie(name: string): string {
  const foundCookie = decodeURIComponent(document.cookie)
    .split(';')
    .map((cookie) => cookie.trim().split('='))
    .find((keyValue) => keyValue[0] === name);

  return foundCookie ? foundCookie[1] : null;
}

function removeCookie(name: string, paths: string[], domains: string[]): void {
  // must use path to delete the correct cookie
  paths.forEach(function (path) {
    domains.forEach(function (domain) {
      document.cookie = `${name}=; expires=Thu, 01-Jan-70 00:00:01 GMT; path=${path}; domain=${domain};`;
    });
  });
}

@Component({
  selector: 'sign-in',
  template: `
    <div class="panels-container">
      <div *ngIf="!isSSOUser" class="panel referral">
        <integrate-io-template></integrate-io-template>
      </div>
      <div class="panel">
        <div class="panel-header">
          <div class="panel-logo">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
              width="70"
              height="50"
              viewBox="0 0 420 296"
            >
              <defs>
                <style>
                  .cls-1 {
                    fill: #2d3e4a;
                  }

                  .cls-1,
                  .cls-2 {
                    fill-rule: evenodd;
                  }

                  .cls-2 {
                    fill: url(#linear-gradient);
                  }
                </style>
                <linearGradient
                  id="linear-gradient"
                  x1="197.094"
                  y1="257"
                  x2="197.094"
                  y2="29"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop offset="0" stop-color="#43dcff" />
                  <stop offset="1" stop-color="#25a5ff" />
                </linearGradient>
              </defs>
              <path
                id="Gradient_Fill_1"
                data-name="Gradient Fill 1"
                class="cls-2"
                d="M131,29l57,31c-0.333.667-1.667,1.333-3.65,2.841-3.4,2.022-6.693,4.5-11.35,7.159-11.675-6.182-31.822-17.881-40-22C105.669,64,76.523,78.814,50,96c0,31.663,1,63.337,1,95,27.664,15.332,50.444,29.646,81,48,15-8.666,32-19.334,47-28q-0.5-1-1-2l-16-9V85l9-5,17,11h-2c-2.333,1.333-5.667,3.667-8,5-0.236,23.263-.262,75.732,0,94,4.666,2.666,14.334,8.334,19,11,6.666-3.333,11.334-7.667,18-11V95l-9-4c0.667-1,3.333-2,4-3,6.972-3.67,9.12-4.913,14-7l7,4V200c-6,3.666-11,7.334-17,11,0.667,0,5.333,3,6,3,14.955,9.344,26.726,17.057,41,24,26.664-15.332,55.336-31.668,82-47V95c-13.676-7.1-73.766-42.662-82-47-12.665,6.666-26.335,15.334-39,22-5-3-11-6-16-9,0.333-.667,3.667-2.333,4-3,19.921-10.9,31.359-16.813,52-29,32.33,19,65.67,38,98,57,0.206,24,.191,92.735,0,114-30.7,15.984-66.67,38.668-99,57l-65-37c-22.022,13.927-38.662,24.221-63,37-32.33-18.665-66.67-37.335-99-56V85Zm65,37c6,3,11,6,17,9-0.333.667-5.667,3.333-6,4-6.574,3.808-3.137,1.78-10,6-5.6-3.282-16.333-9.333-16-10C185.666,72.334,191.334,68.666,196,66Z"
              />
            </svg>
          </div>
          <h3 class="panel-title">{{ 'auth.sign_in.title' | translate }}</h3>
        </div>
        <div class="panel-body">
          <div *ngIf="isMFA" style="padding-top: 85px;" class="form-group">
            <label>{{ 'auth.sign_in.labels.enter_mfa' | translate }}</label>
            <span>
              <input
                class="form-control"
                name="mfa_code"
                [(ngModel)]="credentials.mfa_code"
                type="text"
                maxlength="6"
                required
              />
            </span>
          </div>

          <xp-form-validation type="User">
            <form role="form" name="signInForm" #form="ngForm">
              <fieldset *ngIf="!isMFA">
                <div class="form-group">
                  <xp-form-group>
                    <label>{{ 'auth.sign_in.form.labels.email' | translate }}</label>
                    <input
                      class="form-control"
                      [(ngModel)]="credentials.email"
                      [placeholder]="'auth.sign_in.form.placeholders.email' | translate"
                      name="email"
                      type="email"
                      maxlength="254"
                      autofocus
                      required
                    />
                  </xp-form-group>
                </div>
                <div *ngIf="!isSSO" class="form-group">
                  <label>{{ 'auth.sign_in.form.labels.password' | translate }}</label>
                  <span>
                    <input
                      class="form-control"
                      [(ngModel)]="credentials.password"
                      [placeholder]="'auth.sign_in.form.placeholders.password' | translate"
                      name="password"
                      type="password"
                      required
                    />
                  </span>
                </div>
              </fieldset>
              <xp-submit-button
                (submitForm)="submit()"
                classNames="btn-lg btn-block btn-primary"
                [isFormValid]="form.valid"
                [isFormSubmitting]="isLoading"
                [updateText]="'auth.sign_in.form.actions.sign_in' | translate"
              ></xp-submit-button>
            </form>
          </xp-form-validation>
        </div>
        <div *ngIf="!isSSO && !isMFA" class="panel-footer text-center panel-links">
          <a (click)="goToForgotPassword()">{{ 'auth.sign_in.actions.forgot_password' | translate }}</a>
        </div>
        <div *ngIf="!isMFA" class="text-center" style="padding-top: 15px;">
          <a (click)="toggleSSO()">
            {{
              isSSO ? ('auth.sign_in.actions.use_password' | translate) : ('auth.sign_in.actions.use_sso' | translate)
            }}
          </a>
        </div>
      </div>
    </div>
    <div
      id="refcandy-poprocks"
      data-id="s4cqyl9q72g83hkelufvys61s"
      data-location="right"
      data-minimized="no"
      data-version="2"
    ></div>
    <help-menu></help-menu>
    <div
      id="refcandy-mint"
      data-app-id="mq0j07q0r0m9cc5julivrsqou"
      data-fname="John"
      data-lname="Smith"
      data-email="tayler.jones@xplenty.com"
      data-amount="10.99"
      data-currency="USD"
      data-timestamp="1592459525"
      data-external-reference-id="93211001"
      data-signature="3eaf1ede939e989db5f5305911d8fbc6"
    ></div>
    <script>
      (function (e) {
        var t, n, r, i, s, o, u, a, f, l, c, h, p, d, v;
        z = 'script';
        l = 'refcandy-purchase-js';
        c = 'refcandy-mint';
        p = 'go.referralcandy.com/purchase/';
        t = 'data-app-id';
        r = {
          email: 'a',
          fname: 'b',
          lname: 'c',
          amount: 'd',
          currency: 'e',
          'accepts-marketing': 'f',
          timestamp: 'g',
          'referral-code': 'h',
          locale: 'i',
          'external-reference-id': 'k',
          signature: 'ab',
        };
        i = e.getElementsByTagName(z)[0];
        s = function (e, t) {
          if (t) {
            return '' + e + '=' + encodeURIComponent(t);
          } else {
            return '';
          }
        };
        d = function (e) {
          return '' + p + h.getAttribute(t) + '.js?aa=75&';
        };
        if (!e.getElementById(l)) {
          h = e.getElementById(c);
          if (h) {
            o = e.createElement(z);
            o.id = l;
            a = (function () {
              var e;
              e = [];
              for (n in r) {
                u = r[n];
                v = h.getAttribute('data-' + n);
                e.push(s(u, v));
              }
              return e;
            })();
            o.src = '//' + d(h.getAttribute(t)) + a.join('&');
            return i.parentNode.insertBefore(o, i);
          }
        }
      })(document);
    </script>
  `,
})
export class SignInComponent implements OnInit {
  @ViewChild('form') form: NgForm;
  isMFA = false;
  isSSO = false;
  credentials: Credentials = {
    email: '',
    password: '',
    mfa_code: '',
  };
  errorMessage = '';
  isLoading = false;
  isSSOUser = false;

  constructor(
    @Inject('$rootScope') private $rootScope: any,
    private authService: AuthService,
    private http: HttpClient,
    private translate: TranslateService,
    @Inject('$stateParams') private $stateParams: any,
    private notifyService: NotifyService,
    private store: Store<AppState>,
    private pusherService: PusherService,
  ) {
    // @ts-ignore
    window.email_required = true;
  }

  ngOnInit() {
    this.isSSOUser = getSSOFlag();

    if (this.isAuthBySSO()) {
      this.store.dispatch(loginSuccess());
      this.$rootScope.stateGo('app');
    } else if (this.authService.isAuthenticated()) {
      this.$rootScope.stateGo('app');
    } else {
      // no matter if logout was intentional action or
      // session expired because of timeout
      // user will be redirected here
      this.pusherService.unsubscribeUser();
      this.pusherService.unsubscribeAccount();

      if (!this.isSSOUser && getIntegrateIOFlag()) {
        redirectToIntegrateIOLogin();
      }
    }
  }

  goToForgotPassword() {
    this.$rootScope.stateGo('app.auth.forgot');
  }

  isAuthBySSO(): boolean {
    // get the access token from the cookie
    const token = getCookie(cookieName);
    // for future debugging reference, the following evaluate to false in JS:
    // 0, "", null, undefined, NaN
    if (token) {
      removeCookie(cookieName, ['/auth', '/auth/login'], ['xplenty.com', 'localhost']);
      this.authService.setToken(token);
      return true;
    }
    return false;
  }

  toggleSSO() {
    this.isSSO = !this.isSSO;
  }

  submit() {
    if (this.isSSO) {
      this.singleSignOn();
      return;
    }
    this.signIn();
  }

  singleSignOn() {
    const url = `${process.env.API_URL}/auth/auth0/initiate`;
    const body = this.credentials;
    this.isLoading = true;

    this.http.post(url, body).subscribe(
      (response: { url: string }) => {
        this.isLoading = false;
        // status == 200 if we enter here
        if (response.url) {
          window.location.replace(response.url);
        }
      },
      ({ error }) => {
        // the catch part gets called if the server responds unexpectedly or knowingly returns != 200
        this.errorMessage = this.translate.instant('auth.sign_in.labels.invalid_response');
        if (error && error.message) {
          this.errorMessage = error.message;
        }

        this.notifyService.warn(this.errorMessage);
        this.isLoading = false;
      },
    );
  }

  signIn() {
    this.isLoading = true;
    this.authService.login(this.credentials).subscribe(
      (response) => {
        this.isLoading = false;
        if (response && response.mfa_required) {
          this.isMFA = true;
          return;
        }

        this.store.dispatch(loginSuccess());
        if (this.$stateParams.return_url) {
          window.location.replace(this.$stateParams.return_url);
        } else {
          this.$rootScope.stateGo('app.launchpad');
        }
      },
      ({ error }) => {
        this.errorMessage = this.translate.instant('auth.sign_in.labels.invalid_response');

        if (error && error.message) {
          this.errorMessage = error.message;
        }

        this.notifyService.warn(this.errorMessage);
        this.isLoading = false;

        if (this.isMFA) {
          this.isMFA = false;
          this.credentials.mfa_code = '';
        }
      },
    );
  }
}

((window as any).xplenty as angular.IModule).directive(
  'signIn',
  downgradeComponent({
    component: SignInComponent,
  }),
);
